import { Component } from "@theme/component";
import { ThemeEvents } from "@theme/events";
import { morph } from "@theme/morph";
import { onAnimationEnd } from "@theme/utilities";

class StickyAddToCartComponent extends Component {
  requiredRefs = ["stickyBar", "addToCartButton", "quantityDisplay", "quantityNumber"];

  #buyButtonsIntersectionObserver = null;
  #mainBottomObserver = null;
  #resetTimeout;
  #isStuck = false;
  #animationTimeout = null;
  #abortController = new AbortController;
  #targetAddToCartButton = null;
  #currentQuantity = 1;
  #hiddenByBottom = false;
  #loopObserver = null;

  connectedCallback() {
    super.connectedCallback();
    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest(".shopify-section");

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();

    window.addEventListener("scroll", () => {
      window.scrollY > window.innerHeight && !this.#isStuck && !this.#hiddenByBottom && this.#showStickyBar();
    }, { signal, passive: true });

    this.#setupLoopPriceSync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    this.#animationTimeout && clearTimeout(this.#animationTimeout);
    this.#loopObserver?.disconnect();
  }

  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest(".buy-buttons-block");
    if (!buyButtonsBlock) return;

    const footer = document.querySelector("footer") ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    this.#buyButtonsIntersectionObserver = new IntersectionObserver(entries => {
      const [entry] = entries;
      if (entry) {
        if (!entry.isIntersecting && !this.#isStuck) {
          const rect = entry.target.getBoundingClientRect();
          (rect.bottom < 0 || rect.top < 0) && this.#showStickyBar();
        } else if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = false;
          this.#hideStickyBar();
        }
      }
    });

    this.#mainBottomObserver = new IntersectionObserver(entries => {
      const [entry] = entries;
      if (entry) {
        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          const rect = buyButtonsBlock.getBoundingClientRect();
          (rect.bottom < 0 || rect.top < 0) && (this.#hiddenByBottom = false, this.#showStickyBar());
        }
      }
    }, { rootMargin: "200px 0px 0px 0px" });

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;

    this.#targetAddToCartButton.dataset.puppet = "true";
    this.#targetAddToCartButton.click();

    const cartIcon = document.querySelector(".header-actions__cart-icon");
    if (this.refs.addToCartButton.dataset.added !== "true") {
      this.refs.addToCartButton.dataset.added = "true";
    }
    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;

    this.#resetTimeout && clearTimeout(this.#resetTimeout);

    const flyToCartElement = document.createElement("fly-to-cart");
    flyToCartElement.classList.add("fly-to-cart--sticky");
    flyToCartElement.style.setProperty("background-image", `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = "true";
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;
    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute("data-added");
    }, 800);
  };

  #handleVariantUpdate = event => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;
    const newStickyAddToCart = event.detail.data.html.querySelector("sticky-add-to-cart");
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    const currentStuck = this.refs.stickyBar.getAttribute("data-stuck") || "false";
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });
    this.refs.stickyBar.setAttribute("data-stuck", currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    if (variant && variant.id) this.dataset.currentVariantId = variant.id;

    const productForm = this.#getProductForm();
    productForm && (this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]'));

    variant == null && this.#handleVariantUnavailable();
    this.#updateButtonText();
    setTimeout(() => this.#updateLoopPrice(), 50);
  };

  #handleVariantSelected = event => {
    const variantId = event.detail.resource?.id;
    variantId && (this.dataset.currentVariantId = variantId);
  };

  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = "";
    const variantTitleElement = this.querySelector(".sticky-add-to-cart__variant");
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll("input:checked"))
      .map(option => option.value)
      .filter(value => value !== "")
      .join(" / ");

    selectedOptions && (variantTitleElement.textContent = selectedOptions);
  };

  #handleCartAddComplete = _event => {
    this.#targetAddToCartButton && (this.#targetAddToCartButton.dataset.puppet = "false");
  };

  #handleQuantityUpdate = event => {
    event.detail.cartLine || (this.#currentQuantity = event.detail.quantity, this.#updateButtonText());
  };

  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = "true";
  }

  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = "false";
  }

  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest(".shopify-section");
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace("shopify-section-", "");
    return document.querySelector(`#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`);
  }

  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || "1") || 1;
    this.#updateButtonText();
  }

  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;
    const available = !addToCartButton.disabled;
    quantityNumber.textContent = this.#currentQuantity.toString();
    available && this.#currentQuantity > 1 ? quantityDisplay.style.display = "inline" : quantityDisplay.style.display = "none";
  }

  #setupLoopPriceSync() {
    this.#updateLoopPrice();
    setTimeout(() => this.#updateLoopPrice(), 300);
    setTimeout(() => this.#updateLoopPrice(), 800);

    const loopContainer = document.querySelector(".loop-widget-container");
    if (!loopContainer) return;

    this.#loopObserver = new MutationObserver(() => this.#updateLoopPrice());
    this.#loopObserver.observe(loopContainer, {
      attributes: true,
      attributeFilter: ["class"],
      subtree: true,
      childList: true,
    });
  }

  #updateLoopPrice() {
    const priceContainer = this.querySelector(
      '[data-testid="sticky-price-display"] [ref="priceContainer"]'
    );
    if (!priceContainer) return;

    const loopContainer = document.querySelector(".loop-widget-container");
    if (!loopContainer) {
      this.#resetLoopPrice(priceContainer);
      return;
    }

    const selectedOption = loopContainer.querySelector(".loop-widget-purchase-option-selected");
    const isSubscription = selectedOption && !selectedOption.classList.contains("loop-widget-purchase-option-onetime");

    if (!isSubscription) {
      this.#resetLoopPrice(priceContainer);
      return;
    }

    const loopSubPriceEl = loopContainer.querySelector('[id^="loop-widget-purchase-option-price-id-"]');
    let subPriceText = loopSubPriceEl?.innerText?.trim();

    if (!subPriceText) {
      const jsonEl = document.querySelector("#SellingPlanPrices-global");
      if (!jsonEl) return;
      try {
        const data = JSON.parse(jsonEl.textContent);
        const variantData = data[this.dataset.currentVariantId];
        if (!variantData?.plans) return;
        const cheapest = Object.values(variantData.plans).reduce(
          (low, p) => (!low || Number(p.price) < Number(low.price) ? p : low),
          null
        );
        if (!cheapest) return;
        const fmt = window.Shopify?.money_format || "${{amount}}";
        subPriceText = window.Shopify?.formatMoney
          ? window.Shopify.formatMoney(cheapest.price, fmt)
          : "$" + (cheapest.price / 100).toFixed(2);
      } catch {
        return;
      }
    }

    priceContainer.querySelectorAll(".price, .compare-at-price").forEach(el => {
      if (!el.classList.contains("sticky-sub-price")) el.style.display = "none";
    });

    let subEl = priceContainer.querySelector(".sticky-sub-price");
    if (!subEl) {
      subEl = document.createElement("span");
      subEl.className = "sticky-sub-price price";
      priceContainer.appendChild(subEl);
    }
    subEl.textContent = subPriceText;
  }

  #resetLoopPrice(priceContainer) {
    priceContainer.querySelector(".sticky-sub-price")?.remove();
    priceContainer.querySelectorAll(".price, .compare-at-price")
      .forEach(el => (el.style.display = ""));
  }
}

customElements.get("sticky-add-to-cart") || customElements.define("sticky-add-to-cart", StickyAddToCartComponent);